import { useSelectFormById } from "@/features/forms/api/selectFormById";
import { AppPageLayout } from "@/components/common/AppPageLayout";
import { useActionCopy } from "@/features/i18n/actionCopy";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import {
    formPageRouteShellClass,
    formPageShellFillClass,
    publicFormRoutePaddingClass,
} from "@/features/pages/components/formPageLayout";
import { useProcessedScheduleQuery } from "@/features/schedules/api/processSchedule";
import TimingPreview from "@/features/schedules/components/TimingPreview";
import { Skeleton } from "@patient-watch/ui/skeleton";
import { formNameLabel, schedulePreviewLabel } from "@/features/navigation/services/entityDisplayLabel";
import { useScheduleQuery } from "@/features/schedules/api/selectSchedule";
import { useValidationByIdQuery, validationByIdQueryOptions } from "@/features/validations/api/selectValidationById";
import { ValidationComments } from "@/features/validations/comments/components/ValidationComments";
import AdminOnBehalfCallout from "@/features/validations/components/AdminOnBehalfCallout";
import ValidationDecisionFooter from "@/features/validations/components/ValidationDecisionFooter";
import { assignedValidatorLabel, assignedValidatorName } from "@/features/validations/utils/assignedValidatorLabel";
import { readValidationReviewResults } from "@/features/validations/utils/validatorAutoSubmitEmail";
import { useAuth } from "@/utils/auth";
import { useNav } from "@/utils/nav";
import { ui } from "@patient-watch/ui";
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import * as React from "react";

function ValidateItemPage() {
    const { validationId } = Route.useParams();
    const navigate = useNavigate();
    const auth = useAuth();
    const { locale } = useAppLocale();
    const { data: validation, isLoading } = useValidationByIdQuery(validationId);
    const { data: form } = useSelectFormById(
        validation?.entity_type === "form" ? validation.entity_id : "skip",
        locale
    );
    const { data: schedule } = useScheduleQuery(validation?.entity_type === "schedule" ? validation.entity_id : "");
    const identity =
        validation?.entity_type === "schedule"
            ? schedulePreviewLabel(schedule?.timings, locale, "Schedule")
            : formNameLabel(form, validation?.entity_name || "Review");
    const actionCopy = useActionCopy();
    const { data: processedSchedule, isLoading: isProcessing } = useProcessedScheduleQuery(
        validation?.entity_type === "schedule" ? validation.entity_id : ""
    );
    const [returning, setReturning] = React.useState(false);
    const isOnBehalf = Boolean(validation && auth.roles.isAdmin && validation.validated_by !== auth.id);
    const assignedName = assignedValidatorName(validation?.validated_by_name, validation?.validated_by_email);
    const assignedLabel = assignedValidatorLabel(validation?.validated_by_name, validation?.validated_by_email);

    useNav([
        { label: "Validate", route: { to: "/validate" } },
        {
            label: identity,
            route: { to: "/validate/$validationId", params: { validationId } },
        },
    ]);

    const returnToValidate = () => {
        void navigate({ to: "/validate" });
    };

    if (isLoading) {
        return <p className="mx-auto p-4 max-w-4xl text-muted-foreground text-sm">Loading...</p>;
    }

    // Once a sign-off is sent the validation refetches as actioned. Keep the schedule on screen
    // while we head back to the list rather than flashing "already actioned".
    if (!validation || (validation.is_valid !== null && !returning)) {
        return (
            <div className="mx-auto p-4 max-w-4xl text-center">
                <p className="mb-4 text-muted-foreground">This item has already been actioned or no longer exists.</p>
                <ui.Button asChild variant="outline">
                    <Link to="/validate">Back to your list</Link>
                </ui.Button>
            </div>
        );
    }

    return (
        <AppPageLayout width="md" padding="sm" className={`${publicFormRoutePaddingClass} ${formPageRouteShellClass}`}>
            <div className={formPageShellFillClass}>
                <div className="flex flex-col flex-1 gap-4 min-w-0">
                    {isOnBehalf ? (
                        <AdminOnBehalfCallout assignedName={assignedName} assignedLabel={assignedLabel} />
                    ) : null}
                    <div>
                        <div className="flex justify-between items-center gap-2 mb-1">
                            <div className="text-muted-foreground text-xs">Schedule</div>
                            {validation.entity_type === "schedule" ? (
                                <ValidationComments
                                    entity={{ scheduleId: validation.entity_id }}
                                    validationId={validation.validation_id}
                                />
                            ) : null}
                        </div>
                        <h1 className="mb-2 font-bold text-slate-900 text-2xl">{identity}</h1>
                        {validation.entity_description ? (
                            <p className="mb-2 text-slate-600 text-sm">{validation.entity_description}</p>
                        ) : null}
                        <p className="mb-4 text-slate-600 text-sm">
                            Due dates are shown as if a patient started this schedule today. Check each timepoint
                            matches the source.
                        </p>
                        {isProcessing ? (
                            <div className="space-y-2">
                                <Skeleton className="w-full h-10" />
                                <Skeleton className="w-full h-10" />
                                <Skeleton className="w-full h-10" />
                            </div>
                        ) : processedSchedule?.length ? (
                            <TimingPreview showLocked={false} processedSchedule={processedSchedule} />
                        ) : (
                            <p className="text-muted-foreground text-sm">This schedule has no timepoints.</p>
                        )}
                    </div>
                </div>

                <ValidationDecisionFooter
                    validationId={validation.validation_id}
                    isOnBehalf={isOnBehalf}
                    assignedName={assignedName}
                    assignedLabel={assignedLabel}
                    backLabel={actionCopy.back}
                    onBack={returnToValidate}
                    onReturningChange={setReturning}
                />
            </div>
        </AppPageLayout>
    );
}

export const Route = createFileRoute("/_app/validate/$validationId/")({
    component: ValidateItemPage,
    // Forms are reviewed on their own pages. Send pending form validations straight there,
    // to the results if this tab already has answers, otherwise to the form cover.
    loader: async ({ context, params }) => {
        const validation = await context.queryClient.ensureQueryData(validationByIdQueryOptions(params.validationId));
        if (!validation || validation.is_valid !== null || validation.entity_type !== "form") return;
        const answers = readValidationReviewResults(params.validationId);
        throw redirect({
            to: answers?.length
                ? "/validate/$validationId/forms/$formId/results"
                : "/validate/$validationId/forms/$formId",
            params: { validationId: params.validationId, formId: validation.entity_id },
            replace: true,
        });
    },
});
